import React, { useEffect, useRef } from 'react';

import LinkedInLogo from './linkedin-logo.svg';
import GitHubLogo from './github-logo.svg';
import MastodonLogo from './mastodon-logo.svg';
import './App.scss';

interface AppItem {
  img: string;
  appId?: string;
  url?: string;
  name: string;
  description: string;
}

interface Company {
  name: string;
  flag: string;
  role: string;
  date: string;
  about: string;
  accomplishments: string[];
  techStack: string[];
}

interface Project {
  name: string;
  type: string;
  url: string;
  description: string;
}

interface EducationItem {
  degree: string;
  institution: string;
  date: string;
}

const Apps: AppItem[] = [
  { img: 'vendas-icon.png', appId: 'br.com.uol.ps', name: 'PagSeguro Vendas', description: 'Mobile payment app for merchants' },
  { img: 'smart-pos.webp', url: 'https://pagseguro.uol.com.br/para-seu-negocio/maquininhas/moderninha-smart-2', name: 'Smart POS', description: 'Android-powered payment terminal' },
  { img: 'pagbank-icon.png', appId: 'br.com.uol.ps.myaccount', name: 'PagBank', description: 'Digital banking & payments app' },
  { img: 'clip-icon.png', appId: 'com.payclip.clip', name: 'Clip', description: "Mexico's leading payment solution" },
  { img: 'dosh-icon.png', appId: 'com.dosh.client', name: 'Dosh', description: 'Automatic cashback rewards app' },
  { img: 'mercadopago-icon.png', url: 'https://www.mercadopago.com.br', name: 'Mercado Pago', description: "Latin America's largest fintech" },
]

const PlayStoreUrl = `https://play.google.com/store/apps/details?id=`

const Companies: Company[] = [
  {
    name: 'Mercado Pago',
    flag: '🇧🇷',
    role: 'Technical Lead',
    date: 'Apr/24',
    about: "Mercado Pago is one of Latin America's leading fintech companies, part of Mercado Libre group, providing comprehensive payment solutions across Brazil, Argentina, Colombia, Chile, and Mexico.",
    accomplishments: [
      'Technical leadership in structuring two teams focused on evolving the Smart POS device payment platform',
      'Defined architecture and base framework for segmenting monolithic application into 20+ micro apps',
      'Developed a Launcher that transformed user experience and increased solution scalability',
      'Led and mentored 10+ professionals with direct involvement in mentoring, performance recovery, and technical guidance',
      'Designed distributed architecture for migrating mobile monolith to modular mini apps ecosystem',
      'Coordinated efforts across 20+ micro apps ensuring technical alignment and delivery consistency',
      'Enhanced development processes by creating custom plugin, pipeline integration with DevOps team, and defining application monitoring metrics',
      'Adopted AI strategies following company policy to accelerate both code development and process evolution',
    ],
    techStack: ['Android', 'Kotlin', 'SmartPOS', 'C/C++', 'AOSP', 'AI'],
  },
  {
    name: 'Cardlytics',
    flag: '🇺🇸',
    role: 'Team lead / Mobile developer',
    date: 'Jan/22',
    about: 'Through partnerships with top banks, Cardlytics have a complete view of consumer spend, including purchases they make at competitors. This purchase intelligence is the foundation of all their offered services.',
    accomplishments: [
      'Maintainament and enhancement of their Android library',
      'Coordination and support for the App team',
      'Process and solutions proposals to improve the development cycle',
      'Managed/Mentored other developers in the company',
    ],
    techStack: ['Android', 'Java', 'Kotlin', 'GraphQL', 'Node.JS', 'React.JS'],
  },
  {
    name: 'Clip',
    flag: '🇲🇽',
    role: 'Engineer Manager',
    date: 'Apr/20',
    about: 'Providing both mobile and in-site payment solution, Clip is one of the biggest fintechs in Mexico.',
    accomplishments: [
      'Security assessment for all payment devices of the company',
      'Revision of the development cycle and reorganization of the teams',
      'Improvements on the CI/CD process to comply with the PCI requirements',
      'Implementation of an APM system for all mobile apps',
      'Coordination with many hardware providers',
      'Complete revamp of their SDK',
    ],
    techStack: ['Android', 'Java', 'Kotlin', 'GitHub/Actions'],
  },
  {
    name: 'PagSeguro',
    flag: '🇧🇷',
    role: 'Senior/Lead developer - Mobile',
    date: 'Jan/12',
    about: 'PagSeguro is a fintech company based in Sao Paulo - Brazil that provides payment solutions for their clients. Back this February it opened capital on the NYSE with a huge impact.',
    accomplishments: [
      'Deployed the first mobile payment system in Brazil built from the scratch',
      'Project and implementation for the integration with multiple card reader devices. Bluetooth / Audio Jack port',
      'Responsible for the design and implementation of the CI/CD process',
      'Creation of 2 SDKs for providing mobile payment services to other applications',
      'Lead developer on of the first Smart PoS devices in Brazil (Android embeeded)',
      'Lead developer for the PagBank (Banking App) expansion coordinating the work of more than 30 teams',
    ],
    techStack: ['Android', 'Java', 'Kotlin', 'Jenkins', 'Oracle', 'REST', 'Bitbucket/Stash', 'BitRise', 'BrowserStack'],
  },
  {
    name: 'Petrobras',
    flag: '🇧🇷',
    role: 'Senior developer - Fullstack',
    date: 'Sep/10',
    about: 'Petrobras is an oil company currently controlled by the Brazilian government with operations all around the globe.',
    accomplishments: [
      'Maintainament of several legacy Asp 3.0 systems',
      'Project and implementation of a new inventory fuel control system in refineries, written in Asp.Net C# 3.5',
      'Implemented an Access Control system to improve security in refineries. C# for Windows CE embeeded portable devices',
      'Project for a fuel sample management system using RFID. C# for Windows CE embeeded portable devices',
      'Part of a study group for mobile applications',
    ],
    techStack: ['Asp 3.0', 'Asp.Net', 'C#', 'HTML', 'JavaScript', 'C# for Mobile (Windows CE)', 'Android', 'Java', 'Microsoft SQL Server', 'Oracle', 'ClearCase'],
  },
  {
    name: 'Foster',
    flag: '🇧🇷',
    role: 'Developer - Fullstack',
    date: 'Mar/06',
    about: 'Foster is a marketing agency and software house, part of the WPP group',
    accomplishments: [
      'Developed several promotional websites for famous Brazilian brands using Asp 3.0',
      "Built new features for the company's white label CMS solution, written in Asp.Net C# 3.5",
      'Deployed this CMS with both layout and feature customizations on several clients',
    ],
    techStack: ['Asp 3.0', 'Asp.Net', 'C#', 'Visual Basic (.Net)', 'HTML', 'JavaScript', 'XSLT', 'Microsoft SQL Server', 'VCS/VSS', 'SVN'],
  },
]

const Projects: Project[] = [
  {
    name: 'Screenwise',
    type: 'Web Tool',
    url: 'https://amaro-dev.github.io/android-compare/',
    description: 'A tool to compare different Android screen sizes and visualize how the content will differ in each one',
  },
  {
    name: 'Sonic',
    type: 'Kotlin Library',
    url: 'https://github.com/amaro-dev/sonic',
    description: 'A small wrapper to implement MVI multiplatform projects using Kotlin',
  },
  {
    name: 'C3po',
    type: 'macOS App',
    url: 'https://github.com/amaro-dev/c3po',
    description: 'A macOS desktop application for Android developers to explore, debug, and manage Android devices via ADB with an intuitive interface',
  },
]

const Education: EducationItem[] = [
  { degree: 'Postgraduate degree in Systems Analysis', institution: 'Universidade Presbiteriana Mackenzie', date: 'Nov/10' },
  { degree: 'Microsoft Certification', institution: 'Asp.NET web developer', date: 'Jan/07' },
  { degree: 'Bachelor Computer Engineer degree', institution: 'Universidade Santa Cecília', date: 'Nov/05' },
]

function useReveal(appRef: React.RefObject<HTMLDivElement>) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const root = appRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('revealed'); observer.unobserve(e.target); }
      }),
      { root, threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function App() {
  const appRef = useRef<HTMLDivElement>(null);

  useReveal(appRef);

  return (
    <div className="App" ref={appRef}>

      <nav className="Navbar">
        <div className="Navbar__brand">
          <img src="NewLogo.png" className="Navbar__logo" alt="Amaro" />
        </div>
        <div className="Navbar__links">
          <a href="#portfolio">Portfolio</a>
          <a href="#experience">Experience</a>
          <a href="#projects">Projects</a>
          <a href="#education">Education</a>
        </div>
      </nav>

      <section className="Hero" id="hero">
        <div className="Hero__content">
          <p className="Hero__eyebrow">Systems Architect</p>
          <h1 className="Hero__name">Rodrigo Amaro</h1>
          <p className="Hero__tagline">Developing solutions and people</p>
          <p className="Hero__bio">
            Brazilian systems architect passionate about delivering tech solutions that make everyone's lives better
          </p>
          <div className="Hero__social">
            <a href="https://www.linkedin.com/in/rodrigoamarorodrigues/" target="_blank" rel="noreferrer" aria-label="LinkedIn">
              <img src={LinkedInLogo} alt="LinkedIn" />
            </a>
            <a href="https://github.com/RodrigoAmaroEng" target="_blank" rel="noreferrer" aria-label="GitHub">
              <img src={GitHubLogo} alt="GitHub" />
            </a>
            <a href="https://mastodon.social/@rodrigoamaro@mastodon.social" target="_blank" rel="noreferrer" aria-label="Mastodon">
              <img src={MastodonLogo} alt="Mastodon" />
            </a>
          </div>
        </div>
        <div className="Hero__deco" aria-hidden="true">
          <span className="Hero__deco-code">&lt;/&gt;</span>
        </div>
        <div className="Hero__scroll-hint" aria-hidden="true" />
      </section>

      <section className="Section Section--alt" id="portfolio">
        <div className="Section__inner">
          <h2 className="SectionTitle">Portfolio</h2>
          <div className="AppGrid">
            {Apps.map((it) => (
              <a key={it.img} className="AppCard reveal" target="_blank" rel="noreferrer"
                 href={it.url ?? PlayStoreUrl + it.appId}>
                <img src={it.img} alt={it.name} className="AppCard__icon" />
                <span className="AppCard__name">{it.name}</span>
                <span className="AppCard__desc">{it.description}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="Section" id="experience">
        <div className="Section__inner">
          <h2 className="SectionTitle">Experience</h2>
          <div className="Timeline">
            {Companies.map((company) => (
              <div key={company.name} className="TimelineItem reveal">
                <div className="TimelineItem__marker" aria-hidden="true" />
                <div className="TimelineItem__card">
                  <div className="TimelineItem__header">
                    <span className="TimelineItem__date">{company.date}</span>
                    <span className="TimelineItem__flag">{company.flag}</span>
                  </div>
                  <h3 className="TimelineItem__company">{company.name}</h3>
                  <p className="TimelineItem__role">{company.role}</p>
                  <p className="TimelineItem__about">{company.about}</p>
                  <div className="TimelineItem__highlights">
                    <span className="TimelineItem__label">Highlights</span>
                    <ul>
                      {company.accomplishments.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                  </div>
                  <div className="TimelineItem__stack">
                    <span className="TimelineItem__label">Tech Stack</span>
                    <div className="TagList">
                      {company.techStack.map((t) => <span key={t} className="Tag">{t}</span>)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="Section Section--alt" id="projects">
        <div className="Section__inner">
          <h2 className="SectionTitle">Personal Projects</h2>
          <div className="ProjectList">
            {Projects.map((project) => (
              <a key={project.name} className="ProjectCard reveal"
                 href={project.url} target="_blank" rel="noreferrer">
                <span className="ProjectCard__type">{project.type}</span>
                <h3 className="ProjectCard__name">{project.name}</h3>
                <p className="ProjectCard__desc">{project.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="Section" id="education">
        <div className="Section__inner">
          <h2 className="SectionTitle">Education</h2>
          <div className="EducationList">
            {Education.map((edu) => (
              <div key={edu.degree} className="EduItem reveal">
                <span className="EduItem__date">{edu.date}</span>
                <div className="EduItem__body">
                  <p className="EduItem__degree">{edu.degree}</p>
                  <p className="EduItem__institution">{edu.institution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}

export default App;
